import type React from 'react';
import { createContext, useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'ui/component/Button.tsx';
import { Modal } from 'ui/component/Modal.tsx';
import { ConfirmDialog } from './ConfirmDialog.tsx';
import { PromptDialog } from './PromptDialog.tsx';

type ConfirmState = {
  type: 'confirm';
  message: string;
  title?: string;
  resolve: (value: boolean) => void;
};

type PromptState = {
  type: 'prompt';
  message: string;
  title?: string;
  inputValue: string;
  resolve: (value: string | null) => void;
};

type AlertState = {
  type: 'alert';
  message: string;
  title?: string;
  resolve: () => void;
};

type DialogState = ConfirmState | PromptState | AlertState | null;

export type ConfirmFn = (message: string, title?: string) => Promise<boolean>;
export type PromptFn = (message: string, title?: string) => Promise<string | null>;
export type AlertFn = (message: string, title?: string) => Promise<void>;

const DialogContext = createContext<{ confirm: ConfirmFn; prompt: PromptFn; alert: AlertFn } | null>(null);

export const DialogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const [dialog, setDialog] = useState<DialogState>(null);

  const confirm = useCallback<ConfirmFn>(
    (message, title) =>
      new Promise<boolean>((resolve) => {
        setDialog({ type: 'confirm', message, title, resolve });
      }),
    [],
  );

  const prompt = useCallback<PromptFn>(
    (message, title) =>
      new Promise<string | null>((resolve) => {
        setDialog({ type: 'prompt', message, title, inputValue: '', resolve });
      }),
    [],
  );

  const alert = useCallback<AlertFn>(
    (message, title) =>
      new Promise<void>((resolve) => {
        setDialog({ type: 'alert', message, title, resolve });
      }),
    [],
  );

  const handleConfirmConfirm = () => {
    if (dialog?.type !== 'confirm') return;
    dialog.resolve(true);
    setDialog(null);
  };

  const handleConfirmCancel = () => {
    if (dialog?.type !== 'confirm') return;
    dialog.resolve(false);
    setDialog(null);
  };

  const handlePromptConfirm = () => {
    if (dialog?.type !== 'prompt') return;
    dialog.resolve(dialog.inputValue);
    setDialog(null);
  };

  const handlePromptCancel = () => {
    if (dialog?.type !== 'prompt') return;
    dialog.resolve(null);
    setDialog(null);
  };

  const handlePromptChange = (value: string) => {
    if (dialog?.type !== 'prompt') return;
    setDialog({ ...dialog, inputValue: value });
  };

  const handleAlertClose = () => {
    if (dialog?.type !== 'alert') return;
    dialog.resolve();
    setDialog(null);
  };

  return (
    <DialogContext.Provider value={{ confirm, prompt, alert }}>
      {children}
      {dialog?.type === 'confirm' && <ConfirmDialog isOpen={true} message={dialog.message} title={dialog.title} onConfirm={handleConfirmConfirm} onCancel={handleConfirmCancel} />}
      {dialog?.type === 'prompt' && (
        <PromptDialog
          isOpen={true}
          message={dialog.message}
          title={dialog.title}
          value={dialog.inputValue}
          onChange={handlePromptChange}
          onConfirm={handlePromptConfirm}
          onCancel={handlePromptCancel}
        />
      )}
      {dialog?.type === 'alert' && (
        <Modal isOpen={true} onClose={handleAlertClose}>
          <div className="flex w-[450px] flex-col gap-8">
            <p className="text-center">{dialog.message}</p>
            <div className="mt-6 flex w-full gap-4">
              <Button onClick={handleAlertClose} className="blue">
                {t('actions.confirm')}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </DialogContext.Provider>
  );
};

export const useConfirm = (): ConfirmFn => {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error('useConfirm must be used within DialogProvider');
  return ctx.confirm;
};

export const usePrompt = (): PromptFn => {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error('usePrompt must be used within DialogProvider');
  return ctx.prompt;
};

export const useAlert = (): AlertFn => {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error('useAlert must be used within DialogProvider');
  return ctx.alert;
};
